import type { BillingInfo, BillingStrategyQuery } from '@ctct/domain-layer';
import { BillingStrategy } from '@ctct/domain-layer';

import { useBillingInfo } from '../queries';

export enum PaymentStrategy {
  /** Payment processing is handled by Stripe. */
  Stripe = 'stripe-payments',
  /** Payment processing is handled by the legacy PaaS platform. */
  Paas = 'paas-payments',
}

export type PaymentStrategyQuery =
  | {
      data: null;
      isPending: true;
    }
  | {
      data: PaymentStrategy;
      isPending: false;
    };

export const derivePaymentStrategy = ({
  billingStrategy,
  billingInfo,
}: {
  billingStrategy: BillingStrategy;
  billingInfo?: BillingInfo;
}): PaymentStrategy => {
  if (billingStrategy === BillingStrategy.Stripe) {
    return PaymentStrategy.Stripe;
  }

  return billingInfo?.getUsesStripePayments() ? PaymentStrategy.Stripe : PaymentStrategy.Paas;
};

const isPaymentStrategyPending = ({
  billingStrategy,
  isStripeStatusPending,
  isBillingInfoPending,
}: {
  billingStrategy: BillingStrategy | null;
  isStripeStatusPending: boolean;
  isBillingInfoPending: boolean;
}) => isStripeStatusPending || (billingStrategy === BillingStrategy.InHouse && isBillingInfoPending);

export const usePaymentStrategy = ({
  billingStrategyQuery,
}: {
  billingStrategyQuery: BillingStrategyQuery;
}): PaymentStrategyQuery => {
  const billingInfo = useBillingInfo();
  const isPending =
    !billingStrategyQuery.data ||
    isPaymentStrategyPending({
      billingStrategy: billingStrategyQuery.data,
      isStripeStatusPending: billingStrategyQuery.isPending,
      isBillingInfoPending: billingInfo.isLoading || Boolean(billingInfo.error),
    });

  if (isPending) {
    return { data: null, isPending: true };
  }

  return {
    data: derivePaymentStrategy({
      billingStrategy: billingStrategyQuery.data,
      billingInfo: billingInfo.data,
    }),
    isPending: false,
  };
};
